import { storeToRefs } from 'pinia';
import type { KDSDeviceWithUnit, Site, SiteLocation, StationBrand } from '@centric-os/types';
import useTimeSlots from '../use-time-slots';
import { SitesLocalePrefix } from '../../../enums';
import { StationVisibilityOptions } from '../../../routes/site-details/enums';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useKDSStore } from '@centric-os/stores/sites/use-kds-store';
import { computed } from 'vue';
import { useTranslation } from '@centric-os/helpers';
import { useSiteDetailsStore, useStationConfigStore } from '../../../routes/stores';

/**
 * Composable for handling site details and timeslot management.
 * Provides methods to manage and retrieve data related to station timeslots, delivery destinations, and business unit stations.
 */
export default () => {
  // Set localization
  const { translate } = useTranslation();
  const LOCALE_PREFIX = SitesLocalePrefix.SITE_SINGLE;

  // Stores
  const siteDetailsStore = useSiteDetailsStore();
  const { stationVisibilityFilter, stationBeingEdited, isEditingTimeslotStation } =
    storeToRefs(siteDetailsStore);

  const siteStore = useSiteStore();
  const { site } = storeToRefs(siteStore);

  // Set KDS store
  const kdsStore = useKDSStore();
  const { businessUnits } = storeToRefs(kdsStore);

  const { parseTimeStringIntoMinutes, parseUserDefinedDeliveryTimeslotToString } = useTimeSlots();

  /**
   * Check if a station is currently being edited.
   * @param {string} id - The station ID to check.
   * @returns {boolean} Boolean indicating if the station is being edited.
   */
  const isBeingEdited = (id: string): boolean => {
    return id === stationBeingEdited.value?.id && isEditingTimeslotStation.value;
  };

  /**
   * Get the delivery destinations for a given timeslot.
   * @param {StationBrand} station - The station to get the destinations from.
   * @param {number} [rowIndex=0] - The index of the timeslot row (default is 0).
   * @returns {string[]} An array of delivery destinations.
   */
  const getTimeslotDeliveryDestinations = (station: StationBrand, rowIndex = 0): string[] => {
    const stationConfigStore = useStationConfigStore(
      `config-station-location-${station?.location}`,
    );
    let deliveryDestinations;
    const isUserDefined = station?.timeslots?.delivery_is_user_defined;

    if (isUserDefined) {
      deliveryDestinations =
        station.timeslots?.delivery_user_defined?.[rowIndex]?.delivery_destinations;
      return deliveryDestinations || [];
    }

    if (isBeingEdited(station.id)) {
      deliveryDestinations =
        stationBeingEdited.value.timeslots.delivery_destinations ||
        stationConfigStore?.structuredStationPublicConfigs?.[station?.id]
          ?.delivery_destinations_automatic_timeslot;
    } else {
      deliveryDestinations =
        stationConfigStore?.structuredStationPublicConfigs?.[station?.id]
          ?.delivery_destinations_automatic_timeslot;
    }

    return deliveryDestinations || [];
  };

  /**
   * Check if a timeslot is not configured for a station.
   * @param {StationBrand} station - The station to check the timeslot for.
   * @returns {boolean} A boolean indicating if the timeslot is not configured.
   */
  const isTimeslotNotConfigured = (station: StationBrand): boolean => {
    const deliveryDestinations = getTimeslotDeliveryDestinations(station) || [];
    return deliveryDestinations.length === 0;
  };

  /**
   * Get a formatted text for delivery destinations.
   * @param {string[]} deliveryDestinations - The delivery destinations array.
   * @returns {string} A formatted string of delivery destinations.
   */
  const getDeliveryDestinationsText = (deliveryDestinations: string[]): string => {
    if (!deliveryDestinations?.length) return '';
    if (deliveryDestinations.length === 1) return deliveryDestinations[0];
    return `Multiple(${deliveryDestinations.length})`;
  };

  /**
   * Get a formatted string for delivery timeslot length.
   * @param {StationBrand} station - The station to get the timeslot length from.
   * @returns {string} A formatted string of the timeslot length.
   */
  const getDeliveryTimeslotLengthText = (station: StationBrand): string => {
    return !station.timeslots?.delivery_is_user_defined
      ? `${parseTimeStringIntoMinutes(station.timeslots?.delivery_time)} ${translate(`${LOCALE_PREFIX}.list.table.rows.min`)}`
      : // @ts-expect-error FIXME: Argument of type 'DeliveryUserDefined' is not assignable to parameter of type...
        parseUserDefinedDeliveryTimeslotToString(station.timeslots?.delivery_user_defined?.[0]);
  };

  /**
   * Get a site object with an updated station.
   * @param {Site} site - The current site object.
   * @param {StationBrand} updatedStation - The updated station object.
   * @returns {Site} The updated site object with the new station data.
   */
  const getSiteWithUpdatedStation = (site: Site, updatedStation: StationBrand): Site => {
    const locations = site.locations?.map((location) => {
      if (location.id === updatedStation.location) {
        const brands = location.brands?.map((station) =>
          updatedStation.id === station.id ? updatedStation : station,
        );
        return { ...location, brands };
      }
      return location;
    });
    return { ...site, locations };
  };

  /**
   * Get filtered business unit stations based on visibility.
   * @param {StationBrand[]} stations - The array of stations.
   * @returns {StationBrand[]} An array of filtered stations.
   */
  const getBusinessUnitStations = (stations: StationBrand[]): StationBrand[] => {
    if (!stations) return [];
    return stationVisibilityFilter.value === StationVisibilityOptions.VISIBLE
      ? stations.filter((station) => !station.is?.hidden)
      : stations;
  };

  /**
   * Ensure all business unit stations have a default hidden value.
   * @param {StationBrand[]} stations - The array of stations.
   * @returns {StationBrand[]} An array of modified stations with default hidden values.
   */
  const getAllBusinessUnitStations = (stations: StationBrand[]): StationBrand[] => {
    if (!stations) return [];

    return stations.map((station) => {
      if (station.is && station.is.hidden === undefined) {
        station.is.hidden = false;
      }
      return station;
    });
  };

  /**
   * Computed property to get sorted site locations.
   * @returns {SiteLocation[]} A sorted array of site locations.
   */
  const sortedLocations = computed<SiteLocation[]>(() => {
    const locationsArray = site.value?.locations ? [...site.value.locations] : [];
    return locationsArray.toSorted((a, b) => a.name.localeCompare(b.name));
  });

  const locationsSortedBySequence = computed<SiteLocation[]>(() => {
    const locationsArray = site.value?.locations ? [...site.value.locations] : [];
    return locationsArray.sort((a, b) => a.sequence - b.sequence);
  });

  /**
   * Computed property to get all KDS devices from business units.
   * @returns {KDSDeviceWithUnit[]} An array of devices from business units.
   */
  const devices = computed<KDSDeviceWithUnit[]>(() => {
    if (!businessUnits.value) return [];

    const units = businessUnits.value.units;

    const devices = units.reduce((acc, unit) => {
      const devicesWithUnit = unit.kdsDevices.map((device) => ({
        ...device,
        unitNumber: unit.unitNumber,
      }));
      // @ts-expect-error FIXME: No overload matches this call.
      return acc.concat(devicesWithUnit);
    }, [] as KDSDeviceWithUnit[]);

    return devices;
  });

  return {
    isBeingEdited,
    isTimeslotNotConfigured,
    getTimeslotDeliveryDestinations,
    getDeliveryDestinationsText,
    getDeliveryTimeslotLengthText,
    getSiteWithUpdatedStation,
    getBusinessUnitStations,
    getAllBusinessUnitStations,
    locationsSortedBySequence,
    sortedLocations,
    devices,
  };
};
